// AI-CONTEXT: See docs/features/update-aanvragen.md

import { useState } from 'react'
import firebase from '../../firebase/compat'
import { client } from '../../hooks/Client'
import useSettings from '../../hooks/Settings'
import activeUpdateModules from '../../helpers/updates/activeUpdateModules'
import { ALWAYS_VISIBLE_MODULE, UPDATE_MODULES } from '../../helpers/updates/updateModules'
import {
  UPDATE_REQUEST_SEVERITIES,
  UPDATE_REQUEST_SEVERITY_LABELS,
  UPDATE_REQUEST_SEVERITY_HINTS,
} from '../../helpers/updateRequests/updateRequestSeverity'

const MAX_TITLE_LENGTH = 150
const MAX_BODY_LENGTH = 4000

/**
 * Formulier waarmee een organisatie een update aanvraagt.
 *
 * Het verzoek gaat via de Cloud Function `saveUpdateRequest`, niet rechtstreeks
 * naar Firestore: het document draagt de CompagnyID van de indiener en een
 * status die alleen Deccos mag zetten.
 */
const UpdateRequestForm = () => {

  // State
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [module, setModule] = useState(ALWAYS_VISIBLE_MODULE)
  // Bewust leeg: zonder actieve keuze komt iedereen op dezelfde waarde uit en
  // zegt het veld niets over de urgentie.
  const [severity, setSeverity] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)

  // Hooks
  const settings = useSettings()

  // Zolang de instellingen laden is niet bekend welke onderdelen deze
  // organisatie afneemt, en dus ook niet welke categorieën geldig zijn. Er
  // wordt dan niets getoond — een formulier dat je wél kunt versturen terwijl
  // de keuzelijst nog niet klopt, is een poort die openstaat.
  // Zie docs/pitfalls.md#state-gate-defaults-open-while-loading
  const categorieen = settings.loaded
    ? [ALWAYS_VISIBLE_MODULE, ...activeUpdateModules(settings)]
    : null

  const compleet = title.trim() !== '' && body.trim() !== '' && severity !== ''

  // Verzenden
  const verstuur = async (e) => {
    e.preventDefault()
    if (!compleet || sending) return

    setError('')
    setSending(true)

    try {
      const saveUpdateRequest = firebase.app().functions('europe-west1')
        .httpsCallable('saveUpdateRequest')

      await saveUpdateRequest({ client, Title: title, Body: body, Module: module, Severity: severity })

      setSent(true)
      setTitle('')
      setBody('')
      setSeverity('')
    } catch (err) {
      // De melding van de functie zelf, zodat de gebruiker leest wát er mis is
      // in plaats van een algemene tekst die niets verklaart.
      setError(err.message || 'Het versturen is niet gelukt. Probeer het opnieuw.')
    } finally {
      setSending(false)
    }
  }

  if (!categorieen) return null

  // JSX
  return (
    <form className="update-request-form" onSubmit={verstuur}>
      <p className="update-request-intro">
        Mis je iets, of kan iets handiger? Laat het hier weten. Je ziet de
        aanvraag daarna terug met de stand van zaken.
      </p>

      <label htmlFor="update-request-title">Waar gaat het over?</label>
      <input
        id="update-request-title"
        type="text"
        maxLength={MAX_TITLE_LENGTH}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <label htmlFor="update-request-body">Wat wil je kunnen?</label>
      <textarea
        id="update-request-body"
        rows={6}
        maxLength={MAX_BODY_LENGTH}
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />

      <label htmlFor="update-request-module">Over welk onderdeel gaat het?</label>
      <select
        id="update-request-module"
        value={module}
        onChange={(e) => setModule(e.target.value)}
      >
        {categorieen.map(slug => (
          <option key={slug} value={slug}>{UPDATE_MODULES[slug].label}</option>
        ))}
      </select>

      <label htmlFor="update-request-severity">Hoe erg is het voor je?</label>
      <select
        id="update-request-severity"
        value={severity}
        onChange={(e) => setSeverity(e.target.value)}
      >
        <option value="">— Maak een keuze —</option>
        {UPDATE_REQUEST_SEVERITIES.map(waarde => (
          <option key={waarde} value={waarde}>
            {`${UPDATE_REQUEST_SEVERITY_LABELS[waarde]} — ${UPDATE_REQUEST_SEVERITY_HINTS[waarde]}`}
          </option>
        ))}
      </select>

      <button type="submit" disabled={!compleet || sending}>
        {sending ? 'Bezig met versturen…' : 'Versturen'}
      </button>

      {error &&
        <p className="update-request-error" role="alert">{error}</p>
      }

      {sent && !error &&
        <p className="update-request-sent" role="status">
          We hebben je aanvraag ontvangen. Je vindt hem terug bij je aanvragen.
        </p>
      }
    </form>
  )
}

export default UpdateRequestForm
